import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

interface AdminAuthState {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
}

export function useAdminAuth() {
  const navigate = useNavigate();
  const [state, setState] = useState<AdminAuthState>({
    user: null,
    isAdmin: false,
    loading: true,
  });

  useEffect(() => {
    let mounted = true;

    const checkAdminAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          if (mounted) {
            setState({ user: null, isAdmin: false, loading: false });
            navigate('/admin/login', { replace: true });
          }
          return;
        }

        // Check admin role
        const { data: isAdmin } = await supabase.rpc('has_role', {
          _user_id: session.user.id,
          _role: 'admin'
        });

        if (mounted) {
          if (!isAdmin) {
            navigate('/admin/login', { replace: true });
            setState({ user: session.user, isAdmin: false, loading: false });
          } else {
            setState({ user: session.user, isAdmin: true, loading: false });
          }
        }
      } catch (error) {
        if (mounted) {
          setState({ user: null, isAdmin: false, loading: false });
          navigate('/admin/login', { replace: true });
        }
      }
    };

    // Set up listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        if (mounted) {
          setState({ user: null, isAdmin: false, loading: false });
          navigate('/admin/login', { replace: true });
        }
      } else if (event === 'SIGNED_IN' && session?.user) {
        checkAdminAuth();
      }
    });

    // Then check current session
    checkAdminAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  return { ...state, signOut };
}